import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const SeatingAreasSection = () => {
  const seatingAreas = [
  {
    id: 1,
    name: 'Family Dining Zone',
    capacity: '4-8 people',
    features: ['Spacious tables', 'High chairs available', 'Kid-friendly'],
    image: "https://images.unsplash.com/photo-1651718872037-a8c7851fd362",
    imageAlt: 'Spacious family dining area with large wooden tables, comfortable chairs, high chairs for children, and warm welcoming atmosphere',
    icon: 'Users',
    description: 'Perfect for families and larger groups seeking comfortable dining'
  },
  {
    id: 2,
    name: 'Couples Corner',
    capacity: '2 people',
    features: ['Intimate seating', 'Soft lighting', 'Privacy screens'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_112239272-1764582862334.png",
    imageAlt: 'Intimate couples dining corner with cozy two-person tables, soft romantic lighting, privacy screens, and comfortable cushioned seating',
    icon: 'Heart',
    description: 'Cozy spots designed for intimate conversations and dates'
  },
  {
    id: 3,
    name: 'Group Hangout Area',
    capacity: '6-12 people',
    features: ['Communal tables', 'Social atmosphere', 'Easy conversation'],
    image: "https://images.unsplash.com/photo-1696986293936-d8d080a88f50",
    imageAlt: 'Large group dining area with communal tables, bench seating, social atmosphere, and open layout for easy conversation',
    icon: 'UserPlus',
    description: 'Ideal for friend groups and social gatherings'
  },
  {
    id: 4,
    name: 'Quick Bite Counter',
    capacity: '1-2 people',
    features: ['Bar-style seating', 'Fast service', 'Solo-friendly'],
    image: "https://images.unsplash.com/photo-1656533391150-93d586a9f6f9",
    imageAlt: 'Modern counter seating with bar-style stools, quick service area, individual dining spaces, and efficient layout for solo diners',
    icon: 'Coffee',
    description: 'Perfect for solo diners and quick meals on the go'
  },
  {
    id: 5,
    name: 'Outdoor Patio',
    capacity: '2-6 people',
    features: ['Fresh air', 'String lights', 'Garden view'],
    image: "https://images.unsplash.com/photo-1613685302957-3a6fc45346ef",
    imageAlt: 'Outdoor patio seating with wooden furniture, decorative string lights, lush green plants, and open-air dining under evening sky',
    icon: 'TreePine',
    description: 'Al fresco dining with beautiful outdoor ambience'
  },
  {
    id: 6,
    name: 'Lounge Seating',
    capacity: '3-5 people',
    features: ['Comfortable sofas', 'Low tables', 'Relaxed vibe'],
    image: "https://images.unsplash.com/photo-1574284571463-e2094bedee23",
    imageAlt: 'Comfortable lounge area with plush sofas, low coffee tables, cushions, and relaxed casual atmosphere for informal dining',
    icon: 'Armchair',
    description: 'Casual seating for a laid-back dining experience'
  }];


  return (
    <section className="py-16 bg-[var(--color-background)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 animate-fade-in">
          <h2 className="text-3xl sm:text-4xl font-bold text-[var(--color-foreground)] mb-4">
            Seating Areas
          </h2>
          <p className="text-lg text-[var(--color-muted-foreground)] max-w-2xl mx-auto">
            Find your perfect spot - from intimate corners to social spaces
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {seatingAreas?.map((area, index) =>
          <div
            key={area?.id}
            className="group bg-[var(--color-card)] rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 animate-slide-up"
            style={{ animationDelay: `${index * 100}ms` }}>

              <div className="relative aspect-[4/3] overflow-hidden">
                <Image
                src={area?.image}
                alt={area?.imageAlt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />

                
                <div className="absolute top-4 right-4 w-12 h-12 rounded-full bg-[var(--color-primary)] flex items-center justify-center shadow-lg">
                  <Icon name={area?.icon} size={24} color="#FFFFFF" />
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-xl font-bold text-[var(--color-foreground)]">
                    {area?.name}
                  </h3>
                </div>

                <div className="flex items-center gap-2 mb-4 text-[var(--color-muted-foreground)]">
                  <Icon name="Users" size={16} color="var(--color-primary)" />
                  <span className="text-sm font-medium">{area?.capacity}</span>
                </div>

                <p className="text-sm text-[var(--color-muted-foreground)] mb-4">
                  {area?.description}
                </p>

                <div className="space-y-2">
                  {area?.features?.map((feature, idx) =>
                <div key={idx} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[var(--color-primary)]" />
                      <span className="text-sm text-[var(--color-foreground)]">{feature}</span>
                    </div>
                )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-12 bg-gradient-to-br from-[var(--color-primary)]/10 to-[var(--color-accent)]/10 rounded-2xl p-8 text-center">
          <Icon name="Accessibility" size={48} color="var(--color-primary)" className="mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-[var(--color-foreground)] mb-3">
            Accessibility Features
          </h3>
          <p className="text-[var(--color-muted-foreground)] max-w-2xl mx-auto mb-6">
            All our seating areas are wheelchair accessible with wide aisles, accessible restrooms, and staff trained to assist guests with special needs. We're committed to creating an inclusive dining experience for everyone.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-[var(--color-background)] rounded-lg">
              <Icon name="Wheelchair" size={20} color="var(--color-primary)" />
              <span className="text-sm font-medium text-[var(--color-foreground)]">Wheelchair Access</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-[var(--color-background)] rounded-lg">
              <Icon name="Volume2" size={20} color="var(--color-primary)" />
              <span className="text-sm font-medium text-[var(--color-foreground)]">Hearing Assistance</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-[var(--color-background)] rounded-lg">
              <Icon name="Eye" size={20} color="var(--color-primary)" />
              <span className="text-sm font-medium text-[var(--color-foreground)]">Visual Aids</span>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default SeatingAreasSection;